import { useEffect } from 'react';

export const HEADER_VAR = '--bright-header-height';
export const FOOTER_VAR = '--bright-footer-height';
export const LISTING_HEADER_VAR = '--bright-listing-header-height';

/**
 * Single source of truth for Bright chrome element heights.
 *
 * Observes the mega-menu header, listing header (EditHeader), and footer that
 * are injected into the DOM after the React app renders. Publishes their
 * heights as CSS custom properties on `document.documentElement`:
 *
 *   --bright-header-height          (mega-menu / app header)
 *   --bright-footer-height          (app footer)
 *   --bright-listing-header-height  (EditHeader, when present)
 *
 * Any component can reference these via `var(--bright-header-height, 0px)`
 * without needing JS state or re-renders.
 *
 * Uses ResizeObserver for live height tracking and MutationObserver to detect
 * late injection of elements into the DOM.
 */
export function useBrightChromeObserver() {
  useEffect(() => {
    const root = document.documentElement;
    const resizeObserver = new ResizeObserver(() => measure(root));

    const observed = new WeakSet<Element>();

    function tryObserve(el: Element | null) {
      if (el && !observed.has(el)) {
        observed.add(el);
        resizeObserver.observe(el);
      }
    }

    function findHeader(): Element | null {
      return (
        document.getElementById('bright-megamenu') ||
        document.querySelector('[data-app-header]')
      );
    }

    function findListingHeader(): Element | null {
      return document.querySelector('[data-listing-header]');
    }

    function findFooter(): Element | null {
      return (
        document.querySelector('[data-app-footer]') ||
        document.querySelector('footer')
      );
    }

    function getVisibleHeight(el: Element | null): number {
      if (!el) return 0;
      const styles = window.getComputedStyle(el);
      if (styles.display === 'none' || styles.visibility === 'hidden') return 0;
      return (el as HTMLElement).getBoundingClientRect().height;
    }

    function measure(rootEl: HTMLElement) {
      const header = findHeader();
      const listingHeader = findListingHeader();
      const footer = findFooter();

      rootEl.style.setProperty(HEADER_VAR, `${getVisibleHeight(header)}px`);
      rootEl.style.setProperty(
        LISTING_HEADER_VAR,
        `${getVisibleHeight(listingHeader)}px`,
      );
      rootEl.style.setProperty(FOOTER_VAR, `${getVisibleHeight(footer)}px`);

      tryObserve(header);
      tryObserve(listingHeader);
      tryObserve(footer);
    }

    // Initial measurement
    measure(root);

    // Watch for late DOM injection (mega menu, branding footer, etc.)
    const mutationObserver = new MutationObserver(() => {
      measure(root);
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      root.style.removeProperty(HEADER_VAR);
      root.style.removeProperty(LISTING_HEADER_VAR);
      root.style.removeProperty(FOOTER_VAR);
    };
  }, []);
}
